import { createApi } from '@reduxjs/toolkit/query/react';
import VenueDTO, {
  CreateVenueDTO, SaveVenueLayoutDTO, VenueLayoutDTO, VenueShapeDTO,
} from '../../models/venue';
import VenueSeatDTO, { CreateVenueSeatDTO } from '../../models/venue-seat';
import { Response } from '../../models/shared';
import baseQueryWithReauth from './base';

const venuesApi = createApi({
  reducerPath: 'venuesApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['venues', 'venue_seats', 'venue_layout'],
  endpoints: (build) => ({
    // withSummary adds per-venue counts (places by type, people, zones, events using it).
    getVenues: build.query<VenueDTO[], { withSummary?: boolean } | void>({
      providesTags: ['venues'],
      query: (args) => ({
        url: '/venues',
        method: 'GET',
        params: args && args.withSummary ? { summary: 1 } : undefined,
      }),
      transformResponse: (response: Response<VenueDTO[]>): VenueDTO[] => response.data || [],
    }),
    getVenueById: build.query<VenueDTO | null, { venueId: string }>({
      providesTags: (result, error, { venueId }) => [{ type: 'venues', id: venueId }],
      query: ({ venueId }) => ({
        url: `/venues/${venueId}`,
        method: 'GET',
      }),
      transformResponse: (response: Response<VenueDTO>): VenueDTO | null => response.data || null,
    }),
    getVenueSeats: build.query<VenueSeatDTO[], { venueId: string }>({
      providesTags: (result, error, { venueId }) => [{ type: 'venue_seats', id: venueId }],
      query: ({ venueId }) => ({
        url: `/venues/${venueId}/seats`,
        method: 'GET',
      }),
      transformResponse: (response: Response<VenueSeatDTO[]>): VenueSeatDTO[] => response.data || [],
    }),
    createVenue: build.mutation<VenueDTO | null, CreateVenueDTO>({
      query: (body) => ({
        url: '/venues',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['venues'],
      transformResponse: (response: Response<VenueDTO>): VenueDTO | null => response.data || null,
    }),
    updateVenue: build.mutation<VenueDTO | null, { venueId: string; body: Partial<CreateVenueDTO> }>({
      // The backend validator requires `id` inside the body even though the
      // venue id is also in the URL — match the convention used by other PUT
      // endpoints in this codebase (orders, audience-discounts, etc.).
      query: ({ venueId, body }) => ({
        url: `/venues/${venueId}`,
        method: 'PUT',
        body: { ...body, id: venueId },
      }),
      invalidatesTags: (result, error, { venueId }) => [
        'venues',
        { type: 'venues', id: venueId },
      ],
      transformResponse: (response: Response<VenueDTO>): VenueDTO | null => response.data || null,
    }),
    getVenueLayout: build.query<VenueLayoutDTO | null, { venueId: string }>({
      providesTags: (result, error, { venueId }) => [{ type: 'venue_layout', id: venueId }],
      query: ({ venueId }) => ({
        url: `/venues/${venueId}/layout`,
        method: 'GET',
      }),
      transformResponse: (response: Response<VenueLayoutDTO>): VenueLayoutDTO | null => response.data || null,
    }),
    // Public: stage, walkways, zones… drawn under the places on the buyer map.
    getVenueShapes: build.query<VenueShapeDTO[], { venueId: string }>({
      providesTags: (result, error, { venueId }) => [{ type: 'venue_layout', id: venueId }],
      query: ({ venueId }) => ({
        url: `/venues/${venueId}/shapes`,
        method: 'GET',
      }),
      transformResponse: (response: Response<VenueShapeDTO[]>): VenueShapeDTO[] => response.data || [],
    }),
    saveVenueLayout: build.mutation<VenueLayoutDTO | null, { venueId: string; body: SaveVenueLayoutDTO }>({
      query: ({ venueId, body }) => ({
        url: `/venues/${venueId}/layout`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { venueId }) => [
        'venues',
        { type: 'venues', id: venueId },
        { type: 'venue_layout', id: venueId },
        { type: 'venue_seats', id: venueId },
      ],
      transformResponse: (response: Response<VenueLayoutDTO>): VenueLayoutDTO | null => response.data || null,
    }),
    publishVenue: build.mutation<VenueDTO | null, { venueId: string }>({
      query: ({ venueId }) => ({
        url: `/venues/${venueId}/publish`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { venueId }) => [
        'venues',
        { type: 'venues', id: venueId },
        { type: 'venue_layout', id: venueId },
      ],
      transformResponse: (response: Response<VenueDTO>): VenueDTO | null => response.data || null,
    }),
    duplicateVenue: build.mutation<VenueDTO | null, { venueId: string }>({
      query: ({ venueId }) => ({
        url: `/venues/${venueId}/duplicate`,
        method: 'POST',
      }),
      invalidatesTags: ['venues'],
      transformResponse: (response: Response<VenueDTO>): VenueDTO | null => response.data || null,
    }),
    deleteVenue: build.mutation<void, { venueId: string }>({
      query: ({ venueId }) => ({
        url: `/venues/${venueId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['venues'],
    }),
    bulkCreateVenueSeats: build.mutation<
    VenueSeatDTO[],
    { venueId: string; seats: CreateVenueSeatDTO[] }
    >({
      query: ({ venueId, seats }) => ({
        url: `/venues/${venueId}/seats/multiple`,
        method: 'POST',
        body: { seats },
      }),
      invalidatesTags: (result, error, { venueId }) => [
        { type: 'venue_seats', id: venueId },
      ],
      transformResponse: (response: Response<VenueSeatDTO[]>): VenueSeatDTO[] => response.data || [],
    }),
  }),
});

export const {
  useGetVenuesQuery,
  useGetVenueByIdQuery,
  useGetVenueSeatsQuery,
  useCreateVenueMutation,
  useUpdateVenueMutation,
  useDeleteVenueMutation,
  useBulkCreateVenueSeatsMutation,
  useGetVenueLayoutQuery,
  useGetVenueShapesQuery,
  useSaveVenueLayoutMutation,
  usePublishVenueMutation,
  useDuplicateVenueMutation,
} = venuesApi;
export { venuesApi };
